import { useState, useEffect } from 'react';
import { Mail, Phone, Globe, FileText, ExternalLink, ShieldCheck, CreditCard, CheckCircle2, XCircle, Target, Gauge, TrendingUp, Building2, Users as UsersIcon, BarChart3, Layers, Sparkles, Wallet, StickyNote, ClipboardList, Save, Lock, Trash2 } from 'lucide-react';
import { API_BASE_URL } from '@/utils/env';
import { getToken } from '@/utils/auth';
import { showAlert, showConfirm } from '@/components/AppDialog';
import { clearUsersCache } from '@/utils/adminUsers';
import { database, storage } from '../firebase';
import { ref as storageRef, listAll, getDownloadURL, getMetadata } from 'firebase/storage';
import { BRAND } from '@/brand';
import { ref as dbRef, get } from 'firebase/database';
import { purchasedPackages } from '@/utils/purchases';

/* ClientAccountView — the full 360° of everything a single client submitted, plus the
   admin billing / access / workflow controls. Extracted from the old /admin/user/:uid
   page so it can be reused BOTH as that standalone route AND embedded inside the
   Command Center's "Account" tab (for both consulting & accounting admins).
   Each section renders only if the client has that data, so nothing shows empty.
   Brand-aware via BRAND: the accounting admin sees accounting sections, the consulting
   admin sees consulting sections. */

type AnyObj = Record<string, any>;

const GOAL_NAMES: Record<string, string> = { G1: 'Capital Readiness', G2: 'MVP Launch', G3: 'Revenue Growth', G4: 'Governance & Structure' };
const CR_Q: Record<string, string> = {
  q1: 'Runway', q2: 'Raise amount', q3: 'Annual operating cost', q4: 'Valuation / cap', q5: 'Breakeven timing', q6: 'Next milestone', q7: 'Has debt', q8: 'Debt type', q9: 'Revenue stability', q10: 'Setback response', q11: 'Founder experience', q12: 'Team strengths', q13: 'Decision style', q14: 'Risk appetite',
};

// Order / engagement workflow states (shared by corporate orders + consulting engagements).
const ORDER_STATUSES: { key: string; label: string; tone: string }[] = [
  { key: 'new',           label: 'New',            tone: 'slate' },
  { key: 'in_progress',   label: 'In progress',    tone: 'blue'  },
  { key: 'awaiting_docs', label: 'Awaiting docs',  tone: 'amber' },
  { key: 'delivered',     label: 'Delivered',      tone: 'green' },
  { key: 'cancelled',     label: 'Cancelled',      tone: 'red'   },
];
const statusMeta = (k?: string) => ORDER_STATUSES.find(s => s.key === k) || ORDER_STATUSES[0];

// Data-room folders — Storage is the source of truth (the client uploads there);
// the RTDB mirror is only best-effort, so read Storage to see everything.
const DR_CATS: { id: string; label: string }[] = [
  { id: 'acc-tax', label: 'Tax Documents' },
  { id: 'acc-licence', label: 'Commercial Licence' },
  { id: 'Pitch Materials', label: 'Pitch Materials' },
  { id: 'Financials', label: 'Financials' },
  { id: 'Legal', label: 'Legal' },
  { id: 'Product', label: 'Product' },
];
const DR_LABEL = (id: string) => DR_CATS.find(c => c.id === id)?.label || humanize(id);
// Accounting service names a package's `flags` array maps to (mirrors /my-services).
const SVC_NAMES = ['Bookkeeping', 'VAT Registration', 'VAT Filing', 'Corporate Tax Registration', 'Corporate Tax Filing'];
const asArray = (v: any): any[] => (Array.isArray(v) ? v : v && typeof v === 'object' ? Object.values(v) : []);
const toneBtn = (tone: string) => (({
  slate: 'border-slate-300 bg-slate-100 text-slate-700',
  blue:  'border-blue-300 bg-blue-100 text-blue-700',
  amber: 'border-amber-300 bg-amber-100 text-amber-700',
  green: 'border-green-300 bg-green-100 text-green-700',
  red:   'border-red-300 bg-red-100 text-red-700',
} as Record<string, string>)[tone] || 'border-slate-300 bg-slate-100 text-slate-700');

// ── formatting helpers ──────────────────────────────────────────────────────────
const isIso = (v: any) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v);
const fmtDate = (v: any) => { try { return new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return String(v); } };
const humanize = (k: string) => k.replace(/([A-Z])/g, ' $1').replace(/[_-]/g, ' ').replace(/^./, c => c.toUpperCase()).trim();
function fmtVal(v: any): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (isIso(v)) return fmtDate(v);
  if (typeof v === 'number') return v.toLocaleString('en-US');
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}
const num = (v: any) => (typeof v === 'number' ? v.toLocaleString('en-US') : v ?? '—');

// ── small UI ─────────────────────────────────────────────────────────────────────
function Chip({ children, tone = 'slate' }: { children: React.ReactNode; tone?: string }) {
  const map: Record<string, string> = {
    slate: 'bg-slate-100 text-slate-700', green: 'bg-green-100 text-green-700', red: 'bg-red-100 text-red-700',
    amber: 'bg-amber-100 text-amber-700', blue: 'bg-blue-100 text-blue-700', violet: 'bg-violet-100 text-violet-700',
  };
  return <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${map[tone] || map.slate}`}>{children}</span>;
}

// An access/billing switch used in the client control panel.
function AccessToggle({ label, hint, on, busy, onToggle }: { label: string; hint?: string; on: boolean; busy: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      disabled={busy}
      className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border text-left transition disabled:opacity-60 ${on ? 'border-green-300 bg-green-50/60' : 'border-gray-200 bg-white hover:border-gray-300'}`}
    >
      <span className="min-w-0">
        <span className="block text-sm font-bold text-gray-900">{label}</span>
        {hint && <span className="block text-[11px] text-gray-400 mt-0.5">{hint}</span>}
      </span>
      <span className={`relative w-11 h-6 rounded-full flex-none transition-colors ${on ? 'bg-green-500' : 'bg-gray-300'}`}>
        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${on ? 'left-[22px]' : 'left-0.5'}`} />
      </span>
    </button>
  );
}

function Section({ title, icon, accent = 'blue', children }: { title: string; icon: React.ReactNode; accent?: string; children: React.ReactNode }) {
  const bar: Record<string, string> = { blue: 'from-blue-500 to-blue-600', amber: 'from-amber-400 to-amber-500', violet: 'from-violet-500 to-violet-600', green: 'from-green-500 to-green-600', slate: 'from-slate-400 to-slate-500' };
  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <div className={`h-1.5 bg-gradient-to-r ${bar[accent] || bar.blue}`} />
      <div className="p-5">
        <div className="flex items-center gap-2 mb-4 text-gray-900"><span className="text-gray-400">{icon}</span><h3 className="text-sm font-extrabold tracking-tight">{title}</h3></div>
        {children}
      </div>
    </div>
  );
}

// grid of label/value from an object (skips given keys + empty/nested)
function KV({ obj, skip = [], labels = {} }: { obj: AnyObj; skip?: string[]; labels?: Record<string, string> }) {
  const entries = Object.entries(obj || {}).filter(([k, v]) => !skip.includes(k) && typeof v !== 'object');
  if (!entries.length) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
      {entries.map(([k, v]) => (
        <div key={k} className="flex flex-col gap-0.5">
          <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">{labels[k] || humanize(k)}</span>
          <span className="text-sm font-semibold text-gray-900 break-words">{fmtVal(v)}</span>
        </div>
      ))}
    </div>
  );
}

function Ring({ value, max = 100, color = '#1976d2', sub }: { value: number; max?: number; color?: string; sub?: string }) {
  const pct = Math.max(0, Math.min(1, (value || 0) / max));
  const r = 44, c = 2 * Math.PI * r;
  return (
    <div className="relative w-[110px] h-[110px] flex-none">
      <svg width="110" height="110" viewBox="0 0 110 110" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="55" cy="55" r={r} fill="none" stroke="#EEF2F7" strokeWidth="10" />
        <circle cx="55" cy="55" r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct)} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-black text-gray-900 leading-none">{Math.round(value)}</span>
        <span className="text-[10px] font-bold text-gray-400">{sub || `/ ${max}`}</span>
      </div>
    </div>
  );
}

const scoreColor = (s: number) => (s >= 75 ? '#15824E' : s >= 56 ? '#2E83E6' : s >= 41 ? '#E6B400' : '#E5533C');

// local File icon (lucide `File` re-export kept minimal)
function FileIcon({ className }: { className?: string }) {
  return <FileText className={className} />;
}

interface Props {
  /** The client uid to load. */
  uid?: string;
  /** Embedded inside another page (Command Center) — drops the max-width page wrapper. */
  embedded?: boolean;
  /** Called after the client is permanently deleted (parent decides where to go). */
  onDeleted?: () => void;
}

export default function ClientAccountView({ uid, embedded = false, onDeleted }: Props) {
  const [u, setU] = useState<AnyObj | null>(null);
  const [loading, setLoading] = useState(true);
  const [dataRoom, setDataRoom] = useState<Record<string, any[]>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [noteSaved, setNoteSaved] = useState(false);
  const [payAmt, setPayAmt] = useState('');
  const [payCur, setPayCur] = useState<'AED' | 'USD'>('AED');
  const [payMethod, setPayMethod] = useState('bank_transfer');
  const [payNote, setPayNote] = useState('');

  const adminEmail = (() => { try { return JSON.parse(localStorage.getItem('adminUser') || '{}').email || null; } catch { return null; } })();

  // Merge partial fields into the client's record (admin-on-behalf). The backend
  // audit-logs access/billing/workflow changes automatically.
  const patch = async (fields: AnyObj, label: string): Promise<boolean> => {
    if (!uid) return false;
    setSaving(label);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/auth/user/${uid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(fields),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || (json && json.success === false)) throw new Error(json?.error || `Save failed (${res.status})`);
      setU(prev => ({ ...(prev || {}), ...fields }));
      clearUsersCache(); // so the client list reflects the change on next visit
      return true;
    } catch (e: any) {
      showAlert(e.message || 'Save failed — please try again.');
      return false;
    } finally {
      setSaving(null);
    }
  };

  const recordManualPayment = async () => {
    const amt = parseFloat(payAmt);
    if (!amt || amt <= 0) { showAlert('Enter a payment amount.'); return; }
    const entry = { amount: amt, currency: payCur, method: payMethod, note: payNote || null, at: new Date().toISOString(), by: adminEmail };
    const list = [...asArray(u?.manualPayments), entry];
    const ok = await patch({ manualPayments: list, paymentStatus: 'paid' }, 'payment');
    if (ok) { setPayAmt(''); setPayNote(''); }
  };

  const saveNotes = async () => {
    const ok = await patch({ adminNotes: notes }, 'notes');
    if (ok) { setNoteSaved(true); setTimeout(() => setNoteSaved(false), 2500); }
  };

  // Permanently delete the client — removes the DB record AND the Firebase Auth login.
  const deleteClient = async () => {
    if (!uid) return;
    const label = `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || u?.email || 'this client';
    const confirmed = await showConfirm(
      `Permanently delete ${label}? This removes their account, login and data. This cannot be undone.`,
      { title: 'Delete client', confirmText: 'Delete permanently', cancelText: 'Cancel', destructive: true }
    );
    if (!confirmed) return;
    setSaving('delete');
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/auth/admin/delete-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ uid }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || (json && json.success === false)) throw new Error(json?.error || `Delete failed (${res.status})`);
      clearUsersCache();
      await showAlert(`${label} has been deleted.`, { variant: 'success' });
      onDeleted?.();
    } catch (e: any) {
      showAlert(e.message || 'Delete failed — please try again.');
    } finally {
      setSaving(null);
    }
  };

  useEffect(() => {
    if (!uid) return;
    setU(null);
    (async () => {
      setLoading(true);
      try {
        const token = getToken();
        const headers: HeadersInit = { 'Content-Type': 'application/json' };
        if (token) (headers as AnyObj).Authorization = `Bearer ${token}`;
        const res = await fetch(`${API_BASE_URL}/auth/user/${uid}`, { headers });
        if (res.ok) { const data = await res.json(); setU({ _id: uid, ...(data.user || {}) }); setNotes(data.user?.adminNotes || ''); }
      } catch (e) { console.error('fetch user failed', e); }
      finally { setLoading(false); }
      // Data room — read from Firebase STORAGE (the source of truth the client uploads to);
      // the RTDB node is only a best-effort mirror and is usually empty.
      try {
        const out: Record<string, any[]> = {};
        await Promise.all(DR_CATS.map(async ({ id }) => {
          try {
            const listing = await listAll(storageRef(storage, `dataRoom/${uid}/${id}`));
            const files = await Promise.all(listing.items.map(async item => {
              const [url, meta] = await Promise.all([
                getDownloadURL(item).catch(() => ''),
                getMetadata(item).catch(() => null as any),
              ]);
              return {
                name: meta?.customMetadata?.originalName || item.name.replace(/^\d+_/, ''),
                url,
                uploadedAt: meta?.timeCreated || '',
                size: meta?.size || 0,
                uploadedBy: meta?.customMetadata?.uploadedBy || null,
              };
            }));
            const withUrl = files.filter(f => f.url);
            if (withUrl.length) out[id] = withUrl.sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());
          } catch { /* folder doesn't exist */ }
        }));
        // Merge any RTDB-recorded files for categories Storage didn't return.
        try {
          const snap = await get(dbRef(database, `dataRoom/${uid}`));
          if (snap.exists()) {
            const cats = snap.val() as AnyObj;
            Object.entries(cats).forEach(([cat, files]) => {
              if (!out[cat] && files && typeof files === 'object') out[cat] = Object.entries(files as AnyObj).map(([id, f]) => ({ id, ...(f as AnyObj) }));
            });
          }
        } catch { /* ignore RTDB fallback errors */ }
        setDataRoom(out);
      } catch (e) { console.warn('data room read failed', e); }
    })();
  }, [uid]);

  if (loading) return (
    <div className="flex items-center justify-center py-24"><div className="text-gray-400 font-medium">Loading client…</div></div>
  );
  if (!u) return (
    <div className="flex items-center justify-center py-24"><div className="text-gray-400 font-medium">Client not found.</div></div>
  );

  const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Client';
  const initials = (u.firstName?.[0] || u.email?.[0] || '?').toUpperCase() + (u.lastName?.[0] || '').toUpperCase();
  // Theme + section visibility follow the ADMIN DOMAIN (this admin panel's brand),
  // NOT the individual user's brand — so the accounting admin never shows consulting
  // sections and vice-versa.
  const isCorp = BRAND === 'corporate';
  const showConsulting = !isCorp; // consulting-only sections (goal/IR/capital/persona)
  const showAccounting = isCorp;  // accounting-only sections (business setup order)
  const rj = u.readinessJourney || {};
  const financialYears = u.financials ? Object.keys(u.financials).sort().reverse() : [];
  const kpiYears = u.kpis ? Object.keys(u.kpis).sort().reverse() : [];
  const drCats = Object.keys(dataRoom).filter(c => dataRoom[c]?.length);

  return (
    <div className={embedded ? 'space-y-4' : 'px-4 md:px-6 py-6 max-w-5xl mx-auto space-y-4'}>

      {/* Hero */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-white text-xl font-black flex-none ${isCorp ? 'bg-amber-500' : 'bg-blue-600'}`}>{initials}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-extrabold text-gray-900 tracking-tight">{name}</h1>
              <Chip tone={isCorp ? 'amber' : 'blue'}>{isCorp ? 'Corporate' : 'Consulting'}</Chip>
            </div>
            <div className="flex items-center gap-4 flex-wrap mt-1.5 text-sm text-gray-500">
              {u.email && <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{u.email}</span>}
              {(u.phone || u.phoneNumber) && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{u.phone || u.phoneNumber}</span>}
              {u.country && <span className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" />{u.country}</span>}
            </div>
          </div>
          <button onClick={deleteClient} disabled={saving === 'delete'} title="Delete client" className="flex items-center gap-2 border border-red-200 text-red-600 font-bold text-xs px-3 py-2 rounded-lg hover:bg-red-50 disabled:opacity-50 flex-none self-start">
            <Trash2 className="w-3.5 h-3.5" /> {saving === 'delete' ? 'Deleting…' : 'Delete'}
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap mt-4">
          {u.emailVerified ? <Chip tone="green"><ShieldCheck className="w-3 h-3" />Verified</Chip> : <Chip tone="red"><XCircle className="w-3 h-3" />Unverified</Chip>}
          <Chip tone={u.paymentStatus === 'paid' ? 'green' : 'slate'}><CreditCard className="w-3 h-3" />{u.paymentStatus === 'paid' ? 'Paid' : 'Not paid'}</Chip>
          {showAccounting && u.accountingActivated && <Chip tone="amber">Accounting active</Chip>}
          {showConsulting && u.consultingActivated && <Chip tone="blue">Consulting active</Chip>}
          {showConsulting && u.capitalReportUnlocked && <Chip tone="violet">Report unlocked</Chip>}
          {u.orderStatus && <Chip tone={statusMeta(u.orderStatus).tone}>{statusMeta(u.orderStatus).label}</Chip>}
          {u.authProvider && <Chip>{u.authProvider === 'google' ? 'Google sign-in' : 'Email sign-in'}</Chip>}
          {u.createdAt && <Chip>Joined {fmtDate(u.createdAt)}</Chip>}
          {u.lastLoginAt && <Chip>Last seen {fmtDate(u.lastLoginAt)}</Chip>}
        </div>
      </div>

      {/* ── ADMIN CONTROL: Billing, Access & Workflow ─────────────────── */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="h-1.5 bg-gradient-to-r from-emerald-500 to-teal-500" />
        <div className="p-5 space-y-5">
          <div className="flex items-center gap-2 text-gray-900"><Wallet className="w-4 h-4 text-gray-400" /><h3 className="text-sm font-extrabold tracking-tight">Billing, Access &amp; Workflow</h3></div>

          {/* Access toggles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <AccessToggle label="Payment received" hint="Marks the account as paid" on={u.paymentStatus === 'paid'} busy={saving === 'paid'} onToggle={() => patch({ paymentStatus: u.paymentStatus === 'paid' ? 'unpaid' : 'paid' }, 'paid')} />
            {showConsulting && <AccessToggle label="Capital report unlocked" hint="Grants the founder diagnostic report" on={!!u.capitalReportUnlocked} busy={saving === 'report'} onToggle={() => patch({ capitalReportUnlocked: !u.capitalReportUnlocked }, 'report')} />}
          </div>

          {/* Order / engagement status */}
          <div>
            <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5" />Order / engagement status</div>
            <div className="flex flex-wrap gap-2">
              {ORDER_STATUSES.map(s => {
                const active = (u.orderStatus || 'new') === s.key;
                return (
                  <button key={s.key} disabled={saving === 'status'} onClick={() => patch({ orderStatus: s.key }, 'status')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition disabled:opacity-60 ${active ? toneBtn(s.tone) : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Record a manual payment (no live checkout yet) */}
          <div className="rounded-xl border border-gray-200 p-4 bg-gray-50/50">
            <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" />Record a manual payment</div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex rounded-lg border border-gray-200 overflow-hidden bg-white">
                <select value={payCur} onChange={e => setPayCur(e.target.value as any)} className="px-2 py-2 text-sm font-bold text-gray-700 border-r border-gray-200 focus:outline-none">
                  <option>AED</option><option>USD</option>
                </select>
                <input value={payAmt} onChange={e => setPayAmt(e.target.value)} inputMode="decimal" placeholder="Amount" className="px-3 py-2 text-sm w-28 focus:outline-none" />
              </div>
              <select value={payMethod} onChange={e => setPayMethod(e.target.value)} className="px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white focus:outline-none">
                <option value="bank_transfer">Bank transfer</option>
                <option value="card">Card</option>
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
                <option value="other">Other</option>
              </select>
              <input value={payNote} onChange={e => setPayNote(e.target.value)} placeholder="Reference / note (optional)" className="flex-1 min-w-[160px] px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white focus:outline-none" />
              <button onClick={recordManualPayment} disabled={saving === 'payment'} className="px-4 py-2 rounded-lg bg-[#17263A] text-white text-sm font-bold hover:bg-[#22344d] disabled:opacity-60">
                {saving === 'payment' ? 'Recording…' : 'Record payment'}
              </button>
            </div>
            <p className="text-[11px] text-gray-400 mt-2 flex items-center gap-1"><Lock className="w-3 h-3" />Online checkout (Stripe) isn't enabled yet — use this to log payments taken offline. Marks the account as paid.</p>
            {asArray(u.manualPayments).length > 0 && (
              <div className="mt-3 space-y-1.5">
                {asArray(u.manualPayments).slice().reverse().map((p: AnyObj, i: number) => (
                  <div key={i} className="flex items-center justify-between gap-3 text-xs bg-white rounded-lg border border-gray-100 px-3 py-2">
                    <span className="font-bold text-gray-900">{p.currency} {num(p.amount)}</span>
                    <span className="text-gray-500 flex-1 truncate">{humanize(p.method || '')}{p.note ? ` · ${p.note}` : ''}</span>
                    <span className="text-gray-400 flex-none">{p.at ? fmtDate(p.at) : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Referral attribution (who referred this client) */}
          {u.referredBy && (
            <div className="mb-4">
              <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-1">Referred by</div>
              <a href={`/admin/user/${u.referredBy}`} className="text-sm font-semibold text-blue-600 hover:underline break-all">{u.referredBy}</a>
            </div>
          )}

          {/* Consultation requests (client asked to speak to an advisor) */}
          {Array.isArray((u as any).consultationRequests) && (u as any).consultationRequests.length > 0 && (
            <div className="mb-4">
              <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Consultation requests ({(u as any).consultationRequests.length})</div>
              <div className="space-y-1.5">
                {[...(u as any).consultationRequests].reverse().slice(0, 5).map((r: any, i: number) => (
                  <div key={i} className="text-sm text-gray-700 bg-blue-50/50 border border-blue-100 rounded-lg px-3 py-2">
                    <div className="font-semibold text-gray-800">{r.topic || 'Consultation'}</div>
                    {r.message && <div className="text-xs text-gray-500 mt-0.5">{r.message}</div>}
                    <div className="text-[11px] text-gray-400 mt-0.5">
                      {r.requestedAt ? new Date(r.requestedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                      {r.phone ? ` · ${r.phone}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Internal notes */}
          <div>
            <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><StickyNote className="w-3.5 h-3.5" />Internal notes <span className="normal-case font-medium text-gray-300">· never shown to the client</span></div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Add a private note about this client…" className="w-full px-3 py-2.5 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 resize-y" />
            <div className="flex items-center gap-3 mt-2">
              <button onClick={saveNotes} disabled={saving === 'notes' || notes === (u.adminNotes || '')} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
                <Save className="w-3.5 h-3.5" /> {saving === 'notes' ? 'Saving…' : 'Save note'}
              </button>
              {noteSaved && <span className="text-xs font-bold text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Saved</span>}
            </div>
          </div>
        </div>
      </div>

      {/* ── CONSULTING DATA (hidden entirely on the accounting admin) ── */}
      {showConsulting && (u.goalAssessment || rj.goalGroup) && (
        <Section title="Goal Assessment" icon={<Target className="w-4 h-4" />} accent="blue">
          <div className="flex flex-wrap items-center gap-3">
            <Chip tone="blue">{(u.goalAssessment?.group || rj.goalGroup) || '—'}</Chip>
            <span className="font-bold text-gray-900">{rj.goalName || GOAL_NAMES[u.goalAssessment?.group || rj.goalGroup] || '—'}</span>
            {rj.persona && <span className="text-sm text-gray-500">· {rj.persona}</span>}
          </div>
          {(u.goalAssessment?.completedAt || rj.completedAt) && <p className="text-xs text-gray-400 mt-2">Completed {fmtDate(u.goalAssessment?.completedAt || rj.completedAt)}</p>}
        </Section>
      )}

      {showConsulting && u.irScore && (
        <Section title="Investor Readiness (IR) Score" icon={<Gauge className="w-4 h-4" />} accent="blue">
          <div className="flex items-center gap-5">
            <Ring value={u.irScore.totalScore || 0} color={scoreColor(u.irScore.totalScore || 0)} />
            <div className="flex-1"><KV obj={{ category: u.irScore.category, riskLevel: u.irScore.riskLevel, band: rj.irBand, calculatedAt: u.irScore.calculatedAt }} /></div>
          </div>
        </Section>
      )}

      {showConsulting && u.capitalReadiness && (
        <Section title="Capital Readiness" icon={<Sparkles className="w-4 h-4" />} accent="blue">
          <div className="flex items-center gap-5 mb-4">
            <Ring value={u.capitalReadiness.score || 0} color={scoreColor(u.capitalReadiness.score || 0)} />
            <div className="flex flex-wrap gap-2 items-start">
              {u.capitalReadiness.band && <Chip tone="blue">Band {u.capitalReadiness.band}</Chip>}
              {u.capitalReadiness.dilFlag && <Chip tone="amber">Dilution flag</Chip>}
              {u.capitalReadiness.submittedAt && <Chip>Submitted {fmtDate(u.capitalReadiness.submittedAt)}</Chip>}
            </div>
          </div>
          {u.capitalReadiness.breakdown && typeof u.capitalReadiness.breakdown === 'object' && (
            <div className="space-y-2 mb-4">
              <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">Factor breakdown</div>
              {Object.entries(u.capitalReadiness.breakdown as AnyObj).map(([k, v]) => {
                const val = typeof v === 'number' ? v : 0; const pct = Math.max(0, Math.min(100, val <= 1 ? val * 100 : val));
                return (
                  <div key={k} className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-gray-600 w-40 flex-none">{CR_Q[k] || humanize(k)}</span>
                    <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full bg-blue-500" style={{ width: `${pct}%` }} /></div>
                    <span className="text-xs font-bold text-gray-500 w-10 text-right">{Math.round(pct)}%</span>
                  </div>
                );
              })}
            </div>
          )}
          {u.capitalReadiness.answers && typeof u.capitalReadiness.answers === 'object' && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs font-bold text-blue-700">View raw answers</summary>
              <div className="mt-3"><KV obj={u.capitalReadiness.answers} labels={CR_Q} /></div>
              {Array.isArray(u.capitalReadiness.q12) && u.capitalReadiness.q12.length > 0 && <p className="text-xs text-gray-500 mt-2"><b>Team strengths:</b> {u.capitalReadiness.q12.join(', ')}</p>}
            </details>
          )}
        </Section>
      )}

      {showConsulting && u.capitalAssessment && (
        <Section title="Capital Assessment (intake)" icon={<TrendingUp className="w-4 h-4" />} accent="blue">
          <KV obj={u.capitalAssessment} />
        </Section>
      )}

      {showConsulting && u.raiseCapitalForm && Object.keys(u.raiseCapitalForm).length > 0 && (
        <Section title="Raise-Capital Request" icon={<TrendingUp className="w-4 h-4" />} accent="blue">
          <KV obj={u.raiseCapitalForm} />
        </Section>
      )}

      {/* ── SHARED: company / financials / KPIs ─────────────────────── */}
      {u.company && (
        <Section title="Company Profile" icon={<Building2 className="w-4 h-4" />} accent={isCorp ? 'amber' : 'blue'}>
          <KV obj={u.company} skip={['tradeLicense', 'people']} />
          {u.company.tradeLicense && (
            <div className="mt-4"><div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-2">Trade licence</div><KV obj={u.company.tradeLicense} /></div>
          )}
          {Array.isArray(u.company.people) && u.company.people.length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><UsersIcon className="w-3.5 h-3.5" />People ({u.company.people.length})</div>
              <div className="overflow-x-auto"><table className="w-full text-sm min-w-[420px]"><thead><tr className="text-left text-[11px] font-bold text-gray-400 uppercase tracking-wide">
                <th className="py-1.5 pr-3">Name</th><th className="py-1.5 pr-3">Role</th><th className="py-1.5 pr-3">Nationality</th><th className="py-1.5 pr-3">Ownership</th><th className="py-1.5">UBO</th></tr></thead>
                <tbody>{u.company.people.map((p: AnyObj, i: number) => (
                  <tr key={i} className="border-t border-gray-100"><td className="py-2 pr-3 font-semibold text-gray-900">{p.name || '—'}</td><td className="py-2 pr-3 text-gray-600">{humanize(p.role || '')}</td><td className="py-2 pr-3 text-gray-600">{p.nationality || '—'}</td><td className="py-2 pr-3 text-gray-600">{p.ownershipPct != null ? `${p.ownershipPct}%` : '—'}</td><td className="py-2">{p.isUBO ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : '—'}</td></tr>
                ))}</tbody></table></div>
            </div>
          )}
        </Section>
      )}

      {financialYears.length > 0 && (
        <Section title="Financials" icon={<BarChart3 className="w-4 h-4" />} accent={isCorp ? 'amber' : 'blue'}>
          <div className="overflow-x-auto"><table className="w-full text-sm min-w-[520px]"><thead><tr className="text-left text-[11px] font-bold text-gray-400 uppercase tracking-wide">
            <th className="py-1.5 pr-3">Year</th><th className="py-1.5 pr-3">Revenue</th><th className="py-1.5 pr-3">Expenses</th><th className="py-1.5 pr-3">Cash</th><th className="py-1.5 pr-3">Equity</th><th className="py-1.5">Net profit</th></tr></thead>
            <tbody>{financialYears.map(y => { const f = u.financials[y] || {}; const np = (f.revenue || 0) - (f.totalExpenses || 0); return (
              <tr key={y} className="border-t border-gray-100"><td className="py-2 pr-3 font-bold text-gray-900">{y}</td><td className="py-2 pr-3 text-gray-700">{num(f.revenue)}</td><td className="py-2 pr-3 text-gray-700">{num(f.totalExpenses)}</td><td className="py-2 pr-3 text-gray-700">{num(f.cash)}</td><td className="py-2 pr-3 text-gray-700">{num(f.totalEquity)}</td><td className={`py-2 font-bold ${np >= 0 ? 'text-green-600' : 'text-red-500'}`}>{num(np)}</td></tr>
            ); })}</tbody></table></div>
        </Section>
      )}

      {kpiYears.length > 0 && (
        <Section title="KPIs" icon={<BarChart3 className="w-4 h-4" />} accent={isCorp ? 'amber' : 'blue'}>
          <div className="space-y-4">{kpiYears.map(y => {
            const k = u.kpis[y] || {};
            const groups = ['unit', 'retention', 'growth', 'liquidity'].filter(g => k[g] && typeof k[g] === 'object');
            return (
              <div key={y}>
                <div className="text-xs font-black text-gray-900 mb-2">{y}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {groups.map(g => (
                    <div key={g} className="bg-gray-50 rounded-xl p-3">
                      <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-2">{humanize(g)}</div>
                      <KV obj={k[g]} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}</div>
        </Section>
      )}

      {u.financialHealthCheck && (
        <Section title="Financial Health Check" icon={<BarChart3 className="w-4 h-4" />} accent={isCorp ? 'amber' : 'blue'}>
          <div className="flex items-center gap-5 flex-wrap">
            <Ring value={u.financialHealthCheck.score || 0} color={scoreColor(u.financialHealthCheck.score || 0)} />
            <div className="flex-1 min-w-[240px]">
              <div className="flex flex-wrap gap-2 mb-3">
                {u.financialHealthCheck.band && <Chip tone="blue">{u.financialHealthCheck.band}</Chip>}
                {u.financialHealthCheck.profitable && <Chip tone="green">Profitable</Chip>}
                {u.financialHealthCheck.preRevenue && <Chip tone="slate">Pre-revenue</Chip>}
                {u.financialHealthCheck.computedAt && <Chip>Checked {fmtDate(u.financialHealthCheck.computedAt)}</Chip>}
              </div>
              <KV obj={{
                cash: `${u.financialHealthCheck.currency || ''} ${num(u.financialHealthCheck.cash)}`.trim(),
                avgRevenue: `${u.financialHealthCheck.currency || ''} ${num(u.financialHealthCheck.avgRevenue)}`.trim(),
                expenses: `${u.financialHealthCheck.currency || ''} ${num(u.financialHealthCheck.expenses)}`.trim(),
                runway: u.financialHealthCheck.runway != null ? `${u.financialHealthCheck.runway} mo` : (u.financialHealthCheck.profitable ? 'Profitable' : '—'),
              }} labels={{ avgRevenue: 'Avg monthly revenue', runway: 'Runway' }} />
            </div>
          </div>
        </Section>
      )}

      {/* ── CORPORATE / ACCOUNTING DATA (hidden on the consulting admin) ── */}
      {showAccounting && u.businessSetupData && (
        <Section title="Business Setup Order" icon={<Building2 className="w-4 h-4" />} accent="amber">
          <div className="flex flex-wrap gap-2 mb-3">
            {u.businessSetupData.type && <Chip tone="amber">{u.businessSetupData.type === 'incorporate' ? 'Incorporate (new company)' : 'Operate (existing company)'}</Chip>}
            {u.businessSetupData.hasExpedited && <Chip tone="violet">Expedited</Chip>}
            {u.businessSetupData.hasUpgrade && <Chip tone="green">Total Compliance upgrade</Chip>}
            {u.businessSetupData.total != null && <Chip tone="slate">Total AED {num(u.businessSetupData.total)}</Chip>}
          </div>
          <KV obj={u.businessSetupData} skip={['type', 'hasExpedited', 'hasUpgrade']} />
        </Section>
      )}

      {(() => {
        const pkgList = purchasedPackages(u);
        if (!pkgList.length) return null;
        return (
          <Section title={`Purchased Services (${pkgList.length})`} icon={<Layers className="w-4 h-4" />} accent={isCorp ? 'amber' : 'blue'}>
            <div className="space-y-3">
              {pkgList.map((p: AnyObj, i: number) => {
                const flags = Array.isArray(p.flags) ? p.flags : [];
                const included = SVC_NAMES.filter((_, idx) => flags[idx] === 1);
                return (
                  <div key={i} className="rounded-xl border border-gray-100 bg-gray-50/60 p-3.5">
                    <div className="flex items-center justify-between gap-2 mb-2.5 flex-wrap">
                      <span className="text-sm font-bold text-gray-900">{p.name}</span>
                      <div className="flex items-center gap-2">
                        {p.purchasedAt && <span className="text-[11px] text-gray-400">Since {fmtDate(p.purchasedAt)}</span>}
                        <Chip tone="green"><CheckCircle2 className="w-3 h-3" />Active</Chip>
                      </div>
                    </div>
                    {included.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {included.map(nm => (
                          <span key={nm} className="px-2 py-0.5 bg-white border border-gray-200 text-gray-700 text-[11px] font-semibold rounded-full">{nm}</span>
                        ))}
                        {flags[0] === 1 && p.band && <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-[11px] font-bold rounded-full">{p.band} txns/mo</span>}
                      </div>
                    ) : (
                      <KV obj={p} skip={['flags', 'name', 'purchasedAt', 'price']} />
                    )}
                    {p.price != null && <p className="text-xs text-gray-500 mt-2 font-semibold">Price: {num(p.price)}</p>}
                  </div>
                );
              })}
            </div>
          </Section>
        );
      })()}

      {drCats.length > 0 && (
        <Section title="Data Room" icon={<FileText className="w-4 h-4" />} accent={isCorp ? 'amber' : 'blue'}>
          <div className="space-y-4">{drCats.map(cat => (
            <div key={cat}>
              <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-2">{DR_LABEL(cat)} ({dataRoom[cat].length})</div>
              <div className="space-y-1.5">{dataRoom[cat].map((f, i) => (
                <div key={i} className="flex items-center justify-between gap-3 bg-gray-50 rounded-lg px-3 py-2">
                  <span className="flex items-center gap-2 text-sm font-semibold text-gray-800 truncate"><FileIcon className="w-4 h-4 text-gray-400 flex-none" />{f.originalName || f.name || f.id}</span>
                  {f.url && <a href={f.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-700 flex-none"><ExternalLink className="w-3.5 h-3.5" />Open</a>}
                </div>
              ))}</div>
            </div>
          ))}</div>
        </Section>
      )}

    </div>
  );
}
